import { FC, Fragment } from 'react';
import { selectSortedCasesStructure } from '@core/store/project-slice-selectors';
import classNames from 'classnames';
import { useAppSelector } from '@core/hooks/redux-hooks';
import { useShouldHighlight } from '../custom-hooks/useShouldHighlight';
import { CaseDto, ForecastDto } from '@app/shared/models/contracts/project-dto';
import TableSpacerRow from '@app/shared/components/table/TableSpacerRow';
import { useLocale } from '@app/core/hooks/useLocale';
import { translationKeys } from '@locale/setupI18n';
import { resources } from '@locale/translations';
import { Tooltip, TooltipPlacement } from '@app/shared/components/tooltip/Tooltip';
import { ButtonAppearance, ButtonSize } from '@app/shared/components/button/button-enums';
import Button from '@app/shared/components/button/Button';
import SvgInformation from '@app/shared/icons/Information';
import styles from './table-section-values.module.scss';

type TableSectionValuesPerForecastProps = {
  title: string;
  renderCell: (props: {
    caseItem: CaseDto;
    caseItemIndex: number;
    forecast: ForecastDto;
    forecastIndex: number;
  }) => JSX.Element | null;
  renderAdditionalCell?: (props: { caseItem: CaseDto }) => JSX.Element;
  highlight?: boolean;
  additionalTitleClassNames?: string;
  additionalSpacerTitleClassNames?: string;
  additionalCellClassNames?: string;
  computedCellClassNames?: (props: {
    caseItem: CaseDto;
    caseItemIndex: number;
    forecast: ForecastDto;
    forecastIndex: number;
  }) => string;
  computedAdditionalCellClassNames?: (props: {
    caseItem: CaseDto;
    caseItemIndex: number;
  }) => string;
  renderSpacer?: boolean;
  testId?: string;
  rowTooltipInfo?: string | null;
};

const TableSectionValuesPerForecast: FC<TableSectionValuesPerForecastProps> = ({
  title,
  renderCell,
  renderAdditionalCell,
  highlight,
  additionalTitleClassNames,
  additionalCellClassNames,
  additionalSpacerTitleClassNames,
  computedCellClassNames,
  computedAdditionalCellClassNames,
  renderSpacer = true,
  testId,
  rowTooltipInfo,
}) => {
  const { l } = useLocale();
  const casesStructure = useAppSelector(selectSortedCasesStructure);

  const isTranslationKey = (key: string): key is translationKeys => {
    return Object.keys(resources.en.translations).includes(key);
  };

  const isTitleTranslation = isTranslationKey(title);

  const shouldHighlightCell = useShouldHighlight();

  const renderTooltipButton = (content: string) => (
    <Tooltip content={content} placement={TooltipPlacement.Right} useFixedPositioning>
      <Button appearance={ButtonAppearance.CLEAN} size={ButtonSize.FLUID} aria-label={content}>
        <SvgInformation className={styles['warning-icon']} />
      </Button>
    </Tooltip>
  );

  return (
    <>
      <tr data-testid={testId ? `${title}-${testId}` : undefined}>
        <th
          className={classNames(
            'table-primary__cell--vertical-separator',
            additionalTitleClassNames
          )}>
          {rowTooltipInfo ? (
            <div className={styles['tooltip-header']}>
              {title && isTitleTranslation ? l(title) : title}
              {renderTooltipButton(rowTooltipInfo)}
            </div>
          ) : title && isTitleTranslation ? (
            l(title)
          ) : (
            title
          )}
        </th>
        {casesStructure.map((caseItem, caseItemIndex) => (
          <Fragment key={caseItem.caseId}>
            <td
              className={classNames(
                'table-primary__cell--vertical-separator',
                additionalCellClassNames,
                computedAdditionalCellClassNames?.({
                  caseItem,
                  caseItemIndex,
                })
              )}>
              {renderAdditionalCell !== undefined ? renderAdditionalCell({ caseItem }) : undefined}
            </td>
            {caseItem.forecasts.map((forecast, forecastIndex) => {
              const cellProps = { caseItem, caseItemIndex, forecast, forecastIndex };

              return (
                <td
                  key={forecastIndex}
                  data-testid={testId ? `${testId}-c${caseItemIndex}-f${forecastIndex}` : undefined}
                  className={classNames(
                    additionalCellClassNames,
                    computedCellClassNames?.(cellProps),
                    {
                      'table-primary__cell--vertical-separator':
                        caseItem.forecasts.length - 1 === forecastIndex,
                      'table-primary__cell--highlight':
                        highlight && shouldHighlightCell(caseItem.caseId, forecast.forecastId),
                    }
                  )}>
                  {renderCell(cellProps)}
                </td>
              );
            })}
          </Fragment>
        ))}
      </tr>
      {renderSpacer && (
        <TableSpacerRow additionalTitleClassNames={additionalSpacerTitleClassNames} />
      )}
    </>
  );
};

export default TableSectionValuesPerForecast;
